import { groupBy, mapObjIndexed, prop, sort } from "ramda";

import { ScorecardAnalysisData } from "../../../data/queries/coaching";
import { AttributeExamples, TeamBestPracticeExample, TeamBestPractices } from "../types";

export const LOW_SCORE_MIN_THRESHOLD = 1;
export const LOW_SCORE_MAX_THRESHOLD = 3;
export const HIGH_SCORE_MIN_THRESHOLD = 4;

export const groupByAttribute = (data: ScorecardAnalysisData[]): Record<string, ScorecardAnalysisData[]> =>
  groupBy(prop("attribute"), data) as Record<string, ScorecardAnalysisData[]>;

export const extractLowHighScoring = (
  grouped: Record<string, ScorecardAnalysisData[]>,
  limit: number
): AttributeExamples[] => {
  return Object.entries(grouped).map(([attribute, items]) => {
    const applicableItems = items.filter(item => item.score_value > 0);
    const sortedItems = applicableItems.sort((a, b) => a.score_value - b.score_value);
    const avgScore = applicableItems.length > 0
      ? applicableItems.reduce((sum, item) => sum + item.score_value, 0) / applicableItems.length
      : 0;

    const lowScoring = sortedItems
      .filter(item => item.score_value >= LOW_SCORE_MIN_THRESHOLD && item.score_value <= LOW_SCORE_MAX_THRESHOLD)
      .slice(0, limit);

    const highScoring = sortedItems
      .filter(item => item.score_value >= HIGH_SCORE_MIN_THRESHOLD)
      .reverse()
      .slice(0, limit);

    return {
      attribute,
      lowScoring,
      highScoring,
      avgScore,
      count: applicableItems.length
    };
  });
};

const toTeamBestPracticeExample = (item: ScorecardAnalysisData): TeamBestPracticeExample => ({
  attribute: item.attribute,
  score_value: item.score_value,
  user_email: item.user_email,
  interaction_id: item.interaction_id,
  summary: item.summary,
  details: item.details,
  score_justification: item.score_justification
});

const sortByScoreDesc = sort<TeamBestPracticeExample>((a, b) => b.score_value - a.score_value);

export const extractTopExamples = (
  data: ScorecardAnalysisData[],
  lensName: string,
  examplesPerAttribute: number
): TeamBestPractices | null => {
  if (!data || data.length === 0) {
    return null;
  }

  const examples = data.map(toTeamBestPracticeExample);
  const grouped = groupBy(prop("attribute"), examples);
  const topExamplesByAttribute = mapObjIndexed(
    (items: TeamBestPracticeExample[]) => sortByScoreDesc(items).slice(0, examplesPerAttribute),
    grouped
  ) as Record<string, TeamBestPracticeExample[]>;

  return {
    lens_name: lensName,
    total_conversations: data.length,
    attributes: topExamplesByAttribute
  };
};
